import client from 'client'
import type {
  GetSummarisedTimeSeriesReportForRfDataReportTimeSeriesRfPostMutationRequest,
  GetSummarisedTimeSeriesReportForRfDataReportTimeSeriesRfPostMutationResponse,
  GetSummarisedTimeSeriesReportForRfDataReportTimeSeriesRfPostQueryParams,
  GetSummarisedTimeSeriesReportForRfDataReportTimeSeriesRfPost422,
} from '../../types/GetSummarisedTimeSeriesReportForRfDataReportTimeSeriesRfPost.ts'
import type { QueryKey, QueryObserverOptions, UseQueryResult } from '@tanstack/react-query'
import type { RequestConfig, ResponseErrorConfig, ResponseConfig } from 'client'
import { queryOptions, useQuery } from '@tanstack/react-query'

export const getSummarisedTimeSeriesReportForRfDataReportTimeSeriesRfPostQueryKey = (
  data?: GetSummarisedTimeSeriesReportForRfDataReportTimeSeriesRfPostMutationRequest,
  params?: GetSummarisedTimeSeriesReportForRfDataReportTimeSeriesRfPostQueryParams,
) => [{ url: '/report/time_series/rf' }, ...(params ? [params] : []), ...(data ? [data] : [])] as const

export type GetSummarisedTimeSeriesReportForRfDataReportTimeSeriesRfPostQueryKey = ReturnType<
  typeof getSummarisedTimeSeriesReportForRfDataReportTimeSeriesRfPostQueryKey
>

/**
 * @summary Get Summarised Time Series Report For Rf Data
 * {@link /report/time_series/rf}
 */
export async function getSummarisedTimeSeriesReportForRfDataReportTimeSeriesRfPost(
  data?: GetSummarisedTimeSeriesReportForRfDataReportTimeSeriesRfPostMutationRequest,
  params?: GetSummarisedTimeSeriesReportForRfDataReportTimeSeriesRfPostQueryParams,
  config: Partial<RequestConfig<GetSummarisedTimeSeriesReportForRfDataReportTimeSeriesRfPostMutationRequest>> & { client?: typeof client } = {},
) {
  const { client: request = client, ...requestConfig } = config

  const res = await request<
    GetSummarisedTimeSeriesReportForRfDataReportTimeSeriesRfPostMutationResponse,
    ResponseErrorConfig<GetSummarisedTimeSeriesReportForRfDataReportTimeSeriesRfPost422>,
    GetSummarisedTimeSeriesReportForRfDataReportTimeSeriesRfPostMutationRequest
  >({ method: 'POST', url: `/report/time_series/rf`, params, data, ...requestConfig })
  return res
}

export function getSummarisedTimeSeriesReportForRfDataReportTimeSeriesRfPostQueryOptions(
  data?: GetSummarisedTimeSeriesReportForRfDataReportTimeSeriesRfPostMutationRequest,
  params?: GetSummarisedTimeSeriesReportForRfDataReportTimeSeriesRfPostQueryParams,
  config: Partial<RequestConfig<GetSummarisedTimeSeriesReportForRfDataReportTimeSeriesRfPostMutationRequest>> & { client?: typeof client } = {},
) {
  const queryKey = getSummarisedTimeSeriesReportForRfDataReportTimeSeriesRfPostQueryKey(data, params)
  return queryOptions<
    ResponseConfig<GetSummarisedTimeSeriesReportForRfDataReportTimeSeriesRfPostMutationResponse>,
    ResponseErrorConfig<GetSummarisedTimeSeriesReportForRfDataReportTimeSeriesRfPost422>,
    ResponseConfig<GetSummarisedTimeSeriesReportForRfDataReportTimeSeriesRfPostMutationResponse>,
    typeof queryKey
  >({
    queryKey,
    queryFn: async ({ signal }) => {
      config.signal = signal
      return getSummarisedTimeSeriesReportForRfDataReportTimeSeriesRfPost(data, params, config)
    },
  })
}

/**
 * @summary Get Summarised Time Series Report For Rf Data
 * {@link /report/time_series/rf}
 */
export function useGetSummarisedTimeSeriesReportForRfDataReportTimeSeriesRfPost<
  TData = ResponseConfig<GetSummarisedTimeSeriesReportForRfDataReportTimeSeriesRfPostMutationResponse>,
  TQueryData = ResponseConfig<GetSummarisedTimeSeriesReportForRfDataReportTimeSeriesRfPostMutationResponse>,
  TQueryKey extends QueryKey = GetSummarisedTimeSeriesReportForRfDataReportTimeSeriesRfPostQueryKey,
>(
  data?: GetSummarisedTimeSeriesReportForRfDataReportTimeSeriesRfPostMutationRequest,
  params?: GetSummarisedTimeSeriesReportForRfDataReportTimeSeriesRfPostQueryParams,
  options: {
    query?: Partial<
      QueryObserverOptions<
        ResponseConfig<GetSummarisedTimeSeriesReportForRfDataReportTimeSeriesRfPostMutationResponse>,
        ResponseErrorConfig<GetSummarisedTimeSeriesReportForRfDataReportTimeSeriesRfPost422>,
        TData,
        TQueryData,
        TQueryKey
      >
    >
    client?: Partial<RequestConfig<GetSummarisedTimeSeriesReportForRfDataReportTimeSeriesRfPostMutationRequest>> & { client?: typeof client }
  } = {},
) {
  const { query: queryOptions, client: config = {} } = options ?? {}
  const queryKey = queryOptions?.queryKey ?? getSummarisedTimeSeriesReportForRfDataReportTimeSeriesRfPostQueryKey(data, params)

  const query = useQuery({
    ...(getSummarisedTimeSeriesReportForRfDataReportTimeSeriesRfPostQueryOptions(data, params, config) as unknown as QueryObserverOptions),
    queryKey,
    ...(queryOptions as unknown as Omit<QueryObserverOptions, 'queryKey'>),
  }) as UseQueryResult<TData, ResponseErrorConfig<GetSummarisedTimeSeriesReportForRfDataReportTimeSeriesRfPost422>> & { queryKey: TQueryKey }

  query.queryKey = queryKey as TQueryKey

  return query
}